import E2eeApi from '@/aws/e2ee/E2eeApi'
import { prfProbablySupported } from './PasskeyPrf'

// Rollout policy for passkey (WebAuthn PRF) enrollment, driven by
// VUE_APP_E2EE_PASSKEY_MODE in the .env files:
//
//   optional  (default) — signup OFFERS a passkey with "Skip for now";
//               deferrers are not re-prompted at later logins.
//   required  — signup has no skip, and logins catch up deferred accounts:
//               anyone whose identity bundle has no webauthn wrap (and whose
//               browser holds the cached identity + supports PRF) must
//               complete enrollment before proceeding.
//
// The Account page's layout no longer depends on the mode (2026-10-12): the
// End-to-End Encryption section is always on top, the Legacy passcode row
// always right below it.
//
// There is no `off` (removed 2026-09-21): an unknown value means `optional`.
//
// The plan: ship `optional`, then flip to `required` once the chunk-0 PRF
// support matrix says the user base is ready. Flipping is a webapp
// rebuild+deploy (same as every other VUE_APP_* config).

/**
 * Identity-creation policy, VUE_APP_E2EE_SIGNUP_MODE in the .env files:
 *
 *   optin  (default; user decision 2026-09-11) — signup never mints an NC1
 *           identity. The user sets up encryption from the Account page (or
 *           a dashboard prompt), exactly like Legacy Encryption (Virgil).
 *           Both schemes stay selectable: during the phased app rollout some
 *           devices only support one of them, and an account may hold both.
 *   auto   — the pre-2026-09-11 behaviour: brand-new accounts get their
 *           identity at signup (recovery code + passkey per passkeyMode()).
 */
export function signupMode() {
  const raw = String(process.env.VUE_APP_E2EE_SIGNUP_MODE || '').trim().toLowerCase()
  return raw === 'auto' ? 'auto' : 'optin'
}

export function passkeyMode() {
  const raw = String(process.env.VUE_APP_E2EE_PASSKEY_MODE || '').trim().toLowerCase()
  return raw === 'required' ? 'required' : 'optional'
}

/**
 * Can THIS browser run the NC1 opt-in (the Account page's "Set up a passkey")?
 * Environment-only: the e2ee API is configured and the browser supports the
 * PRF extension. Callers still check the
 * account itself (an NC1 account has nothing to opt into). Used to route the
 * "Set up encryption" deep link (?forceEnableE2ee=true) to the NC1 opt-in
 * instead of the legacy Virgil passcode dialog.
 */
export async function nc1OptInAvailable() {
  if (!E2eeApi.isConfigured()) return false
  try {
    return await prfProbablySupported()
  } catch (e) {
    return false
  }
}
